/**
 * Coups de pouce — Attendu 1113 « Repérer un nombre relatif sur une droite
 * graduée » (automatisme 4e).
 *
 * lire_* (saisie, texte à trous, QCM) : entrée PARTAGÉE — la valeur d'une
 * graduation, le côté (le signe), le comptage depuis le nombre écrit le plus
 * proche, l'écriture. L'exemple : lire l'abscisse de A, placé 3 graduations à
 * gauche de −2 sur une droite en dixièmes : −2,3 (pièges −1,7 et 2,3).
 *
 * vf_tableau_vf : la méthode pour vérifier une affirmation de lecture
 * (signe, valeur, entre quelles graduations, ordre, pas). L'exemple : deux
 * points sur une droite en 0,5, comparer B et C.
 *
 * rappel_tableau_vf : pur rappel, pas d'entrée (doctrine 2026-07-31).
 * Le gen en dixièmes centre ses droites sur −1 à −4 avec des points sur la
 * grille ; l'exemple prend −2,3, mais le geste est le même quel que soit le tirage.
 */

const droite = ({ min, max, pas, reperes, x0 = 20, x1 = 340, y = 150, tick = 7 }) => {
    const px = (v) => x0 + ((v - min) / (max - min)) * (x1 - x0);
    let s = `<line x1="${x0 - 8}" y1="${y}" x2="${x1 + 12}" y2="${y}" stroke="#334155" stroke-width="1.6"/><polygon points="${x1 + 18},${y} ${x1 + 9},${y - 4} ${x1 + 9},${y + 4}" fill="#334155"/>`;
    const n = Math.round((max - min) / pas);
    for (let k = 0; k <= n; k++) {
        const v = Math.round((min + k * pas) * 1000) / 1000;
        const r = reperes.some((q) => Math.abs(q - v) < 1e-9); const h = r ? tick + 3 : tick;
        s += `<line x1="${px(v).toFixed(1)}" y1="${y - h}" x2="${px(v).toFixed(1)}" y2="${y + h}" stroke="#334155" stroke-width="${r ? 1.6 : 1}"/>`;
        if (r) s += `<text x="${px(v).toFixed(1)}" y="${y + 24}" font-family="system-ui,sans-serif" font-size="12" text-anchor="middle" fill="#1e293b">${String(v).replace('.', ',')}</text>`;
    }
    return { svg: s, px };
};
const POINT = (x, y, label, couleur, dy = -12) => `<circle cx="${x.toFixed(1)}" cy="${y}" r="4.5" fill="${couleur}"/><text x="${x.toFixed(1)}" y="${y + dy}" font-family="system-ui,sans-serif" font-size="13" font-weight="800" text-anchor="middle" fill="${couleur}">${label}</text>`;
const FLECHE = (xa, xb, y, couleur) => { const d = xb > xa ? 1 : -1; return `<line x1="${xa.toFixed(1)}" y1="${y}" x2="${(xb - d * 6).toFixed(1)}" y2="${y}" stroke="${couleur}" stroke-width="2.2" stroke-dasharray="5 3"/><polygon points="${xb.toFixed(1)},${y} ${(xb - d * 9).toFixed(1)},${y - 4.5} ${(xb - d * 9).toFixed(1)},${y + 4.5}" fill="${couleur}"/>`; };
const T = (x, y, s, couleur, poids = 700, taille = 11) => `<text x="${x}" y="${y}" font-size="${taille}" font-weight="${poids}" fill="${couleur}">${s}</text>`;
const STYLE = `<style>@keyframes cp1113-pop { from { opacity: 0; } to { opacity: 1; } } .cp1113-pop { opacity: 0; animation: cp1113-pop 0.5s ease forwards; }</style>`;
const CAPTIONS = (c1, c2, c3) => `<text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">${c1}</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">${c2}</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">${c3}</text>`;
const SVG = (aria, captions, e1, e2, e3, h = 300) => `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 ${h}" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3" aria-label="${aria}">
  ${STYLE}
  ${captions}
  <g data-etape="1" font-family="system-ui,sans-serif">${e1}</g>
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif"><g class="cp1113-pop">${e2}</g></g>
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif"><g class="cp1113-pop">${e3}</g></g>
</svg>`.trim();

// Deux couleurs (demande prof 2026-09-11) : l'ÉCART entre les nombres écrits et le nombre de PAS,
// sur la droite et dans le calcul écart ÷ pas. Même moteur que le coup de pouce du 1112.
const C_ECART = '#DC2626'; const C_PAS = '#7C3AED';
/** Entre les nombres écrits a et b : les n pas (sauts violets au-dessus de la droite) et l'écart (accolade rouge plus haut). */
const ECART_ET_PAS = (D, a, b, n) => {
    const x0 = D.px(a); const x1 = D.px(b); const y = 140;
    const saut = (u, v) => `<path d="M ${u.toFixed(1)},${y - 12} Q ${((u + v) / 2).toFixed(1)},${y - 22} ${v.toFixed(1)},${y - 12}" fill="none" stroke="${C_PAS}" stroke-width="1.8"/>`;
    let r = '';
    for (let k = 0; k < n; k++) r += saut(x0 + ((x1 - x0) * k) / n, x0 + ((x1 - x0) * (k + 1)) / n);
    r += `<text x="${((x0 + x1) / 2).toFixed(1)}" y="${y - 25}" font-size="11" font-weight="800" text-anchor="middle" fill="${C_PAS}">${n} pas</text>`;
    r += `<path d="M ${x0.toFixed(1)},${y - 37} L ${x0.toFixed(1)},${y - 41} L ${x1.toFixed(1)},${y - 41} L ${x1.toFixed(1)},${y - 37}" fill="none" stroke="${C_ECART}" stroke-width="1.8"/>`;
    r += `<text x="${((x0 + x1) / 2).toFixed(1)}" y="${y - 45}" font-size="11" font-weight="800" text-anchor="middle" fill="${C_ECART}">écart : ${String(b - a).replace('.', ',')}</text>`;
    return r;
};

// ─── Lire : A à 3 pas à gauche de −2, droite en dixièmes ────────────────────
const D1 = droite({ min: -3, max: -1, pas: 0.1, reperes: [-3, -2, -1], y: 140 });
const SVG_LIRE = SVG(
    "Question : quelle est l'abscisse du point A ? En 3 étapes à faire défiler avec les flèches. Étape 1 : de −3 à −2, l'écart est 1 et il y a 10 pas, donc un pas vaut 1 ÷ 10 = 0,1. Étape 2 : A est à gauche de −2, donc son abscisse est plus petite que −2 ; de −2 à A, on compte 3 pas : 3 fois 0,1, soit 0,3. Étape 3 : on part de −2 et on descend de 0,3 : l'abscisse de A est −2,3, pas −1,7. Réponse : A a pour abscisse −2,3",
    CAPTIONS('Combien vaut un pas ?', 'De quel côté, et combien ?', 'On écrit l\'abscisse'),
    `${T(8, 44, 'Sur cette droite graduée, −3, −2 et −1 sont écrits.', '#1e293b')}
    ${T(8, 60, 'Question : quelle est l\'abscisse du point A ?', '#1E3A8A')}
    ${D1.svg}${POINT(D1.px(-2.3), 140, 'A', '#1E3A8A', 32)}
    ${ECART_ET_PAS(D1, -3, -2, 10)}
    <text x="8" y="196" font-size="11" font-weight="700" fill="#1E3A8A">De −3 à −2, il y a <tspan fill="${C_PAS}">10 pas</tspan> :</text>
    <text x="8" y="210" font-size="11" font-weight="700" fill="#1E3A8A">un pas vaut <tspan fill="${C_ECART}">1</tspan> ÷ <tspan fill="${C_PAS}">10</tspan> = 0,1.</text>`,
    `${FLECHE(D1.px(-2), D1.px(-2.3), 108, '#1D9E75')}
    ${T(8, 234, 'A est à gauche de −2 : son abscisse est plus petite que −2.', '#1D9E75')}${T(8, 248, 'De −2 à A, on compte 3 pas : 3 × 0,1 = 0,3.', '#1D9E75')}`,
    `${T(8, 270, 'On part de −2 et on va de 0,3 vers la gauche : −2,3 (et non −1,7).', '#B45309')}
    ${T(8, 292, 'Réponse : le point A a pour abscisse −2,3.', '#1e293b', 700, 11.5)}`,
);

const AIDE_LIRE = {
    pages: [
        {
            titre: 'La règle',
            lignes: [
                "**D'abord le pas** : prends deux nombres écrits sur la droite et compte combien de pas les séparent. Le pas vaut leur écart divisé par ce nombre : de −3 à −2 en 10 pas, chaque pas vaut 1 ÷ 10 = 0,1 ; de 0 à 1 en 2 pas, chaque pas vaut 0,5.",
                "**Le côté donne le signe** : à gauche de l'origine, l'abscisse est négative et s'écrit avec le signe moins ; à droite, elle est positive.",
                "**Compte depuis le nombre écrit le plus proche** : de −2, trois pas vers la gauche font 0,3 de moins, donc −2,3. Vers la gauche, on s'éloigne de l'origine et le nombre après le signe grandit.",
                "**Écris-la avec la virgule** : −2,3 et pas −2.3 ; −3 et pas −3,0.",
            ],
        },
        { titre: 'Un exemple', svg: SVG_LIRE },
    ],
};

// ─── V/F : B et C sur une droite en 0,5, comparer ───────────────────────────
const D2 = droite({ min: -4, max: 2, pas: 0.5, reperes: [-4, -2, 0, 2], y: 140 });
const SVG_VF = SVG(
    "Question : l'affirmation « l'abscisse du point B est plus petite que celle du point C » est-elle vraie ? En 3 étapes à faire défiler avec les flèches. Étape 1 : de −2 à 0, l'écart est 2 et il y a 4 pas, donc un pas vaut 2 ÷ 4 = 0,5. Étape 2 : B est à 3 pas à gauche de −2, son abscisse est −3,5 ; C est à 1 pas à gauche de 0, son abscisse est −0,5. Étape 3 : B est à gauche de C sur la droite, donc son abscisse est plus petite : −3,5 est plus petit que −0,5. Réponse : l'affirmation est vraie",
    CAPTIONS('Combien vaut un pas ?', 'On lit chaque abscisse', 'On compare'),
    `${T(8, 44, 'Sur cette droite graduée, −4, −2, 0 et 2 sont écrits.', '#1e293b')}
    ${T(8, 60, 'Question : « l\'abscisse de B est plus petite que celle', '#1E3A8A')}${T(8, 74, 'de C ». Vrai ou faux ?', '#1E3A8A')}
    ${D2.svg}${POINT(D2.px(-3.5), 140, 'B', '#1E3A8A')}${POINT(D2.px(-0.5), 140, 'C', '#1E3A8A', 32)}
    ${ECART_ET_PAS(D2, -2, 0, 4)}
    <text x="8" y="196" font-size="11" font-weight="700" fill="#1E3A8A">De −2 à 0, il y a <tspan fill="${C_PAS}">4 pas</tspan> :</text>
    <text x="8" y="210" font-size="11" font-weight="700" fill="#1E3A8A">un pas vaut <tspan fill="${C_ECART}">2</tspan> ÷ <tspan fill="${C_PAS}">4</tspan> = 0,5.</text>`,
    `${T(8, 234, 'B : 3 pas à gauche de −2 : −3,5.', '#1D9E75')}${T(8, 248, 'C : 1 pas à gauche de 0 : −0,5.', '#1D9E75')}`,
    `${T(8, 270, 'B est à gauche de C : son abscisse est plus petite. −3,5 < −0,5 ✓', '#B45309')}
    ${T(8, 292, 'Réponse : l\'affirmation est vraie.', '#1e293b', 700, 11.5)}`,
);

const AIDE_VF = {
    pages: [
        {
            titre: 'La méthode',
            lignes: [
                "**Commence par le pas** : compte les pas entre deux nombres écrits et divise leur écart par ce nombre pour savoir ce que vaut un pas.",
                "**Lis chaque abscisse concernée** : le côté de l'origine donne le signe, le nombre de pas depuis le nombre écrit le plus proche donne la valeur.",
                "**Pour comparer**, regarde la position : le point le plus à gauche a l'abscisse la plus petite. Entre deux négatifs, c'est celui qui est le plus loin de l'origine qui est le plus petit.",
                "**Entre quels nombres écrits** : un point entre −3 et −2 a une abscisse qui commence par −2 virgule quelque chose.",
            ],
        },
        { titre: 'Un exemple', svg: SVG_VF },
    ],
};

const CP_1113 = {
    lire_saisie: AIDE_LIRE,
    lire_texte_trou: AIDE_LIRE,
    lire_qcm_unique: AIDE_LIRE,
    vf_tableau_vf: AIDE_VF,
};

export default CP_1113;
